/* eslint-disable no-dupe-class-members */
import { flow } from '@1x2network/flow'
import { Component, ContainerSpecification, createSpine, Spine, SpineSpecification } from '@1x2network/pixi_assets'
import { Container } from 'pixi.js'

/**
 * Displays the spins remaining during a HAW round
 * @author Jordan Harrison - <jordan.harrison@1x2network.com>
 * @version 1.0.0
 */

export class HawCounter extends Component<HawCounterSpec> {
    private spine: Spine
    private currentSpins = 0
    constructor(parent: Container) {
        super({ parent, spec: 'hawCounter' })
        this.spine = createSpine({ spec: this.specification.spine, parent: this })
    }

    /** animate decrease the spins remaining */
    async decrease() {
        if (this.currentSpins === 0) {
            return
        }

        const current = this.currentSpins
        const next = current - 1
        await flow.wait(this.spine.play(this.animationName('decrease', current, next)))
        this.currentSpins = next
    }

    /** increase the spins remaining */
    private async increase() {
        if (this.currentSpins === 3) {
            return
        }
        const current = this.currentSpins
        const next = 3
        await flow.wait(this.spine.play(this.animationName('increase', current, next)))
        this.currentSpins = next
    }

    /**
     * animate changing spins remaing from one value to another
     * @param amount amount to change to
     */
    public async animateChange(amount: number) {
        if (amount > this.currentSpins) {
            await this.increase()
        }

        if (amount < this.currentSpins) {
            const stages = this.currentSpins - amount
            for (let i = 0; i < stages; i++) {
                await this.decrease()
            }
        }
        this.spine.play(this.animationName('loop', this.currentSpins))
    }

    /**
     * set the spins to the current amount without animating
     * @param amount amount to set to
     */
    setRemaining(amount: number) {
        this.currentSpins = amount
        this.spine.play(this.animationName('loop', this.currentSpins))
    }

    /**
     * create animation name for a loop or static animation
     * @param animation loop or static animations
     * @param current spins remaining to animate
     */
    private animationName(animation: 'loop' | 'static', current: number): string
    /**
     * create animation name for a increase or decrease animation
     * @param animation increase or decrease animation
     * @param from spins from to animate
     * @param to spins to to animate
     */
    private animationName(animation: 'decrease' | 'increase', from: number, to: number): string
    private animationName(animation: 'decrease' | 'increase' | 'loop' | 'static', from: number, to?: number): string {
        if (to !== undefined) {
            return `${animation}_0${from}-0${to}`
        }

        return `${animation}_0${from}`
    }
}

export interface HawCounterSpec extends ContainerSpecification {
    spine: SpineSpecification
}
